import { psCollection } from "./powersync-collection.js";
import { syncApiUrl } from "../sync-api-url.js";
import {
  memberSchema,
  pointTransactionSchema,
  businessLoyaltySettingSchema,
  memberStorePointSchema,
  loyaltyConfigSchema,
  rewardSchema,
  voucherSchema,
  promoSchema,
  type Member,
  type PointTransaction,
  type BusinessLoyaltySetting,
  type MemberStorePoint,
  type LoyaltyConfig,
  type Reward,
  type Voucher,
  type Promo,
} from "../schemas/crm.js";
import { requestHeaders } from "./factory.js";

export const membersCollection = psCollection("members", memberSchema);

export const pointTransactionsCollection = psCollection("point_transactions", pointTransactionSchema);

// Owner-configured toggles ("share customers/points across stores") — has
// a direct business_id column, protected server-side same as core.stores.
export const businessLoyaltySettingsCollection = psCollection("business_loyalty_settings", businessLoyaltySettingSchema);

// The real current-balance table — prefer this over replaying
// point_transactions history to compute a member's balance at a store.
export const memberStorePointsCollection = psCollection("member_store_points", memberStorePointSchema);

export const loyaltyConfigsCollection = psCollection("loyalty_configs", loyaltyConfigSchema);

export const rewardsCollection = psCollection("rewards", rewardSchema);

export const vouchersCollection = psCollection("vouchers", voucherSchema);

/**
 * Spends a member's points on a reward — replaces the old direct
 * `pointTransactionsCollection.insert`/`memberStorePointsCollection.update`/
 * `vouchersCollection.insert`/`rewardsCollection.update` sequence (a
 * client-side read-balance-then-write-it-back race with zero server
 * validation, closed by moving the whole spend server-side, atomic, and
 * guest-scope-authenticated — see services/sync-api/src/routes/crm.ts's
 * `/crm/rewards/:id/redeem`). Those four collections are now hard-locked
 * against direct client writes (member_store_points/point_transactions
 * require a real staff/owner JWT; rewards/vouchers reject every client
 * write outright), so there's no local optimistic update to perform here
 * either — the caller's own balance/voucher-list views catch up via the
 * next Electric shape sync round trip (typically sub-second), same as any
 * other server-driven change this codebase doesn't locally echo.
 */
export async function redeemReward(rewardId: string, memberTicket: string): Promise<{ voucher: Voucher; pointsAfter: number }> {
  const response = await fetch(syncApiUrl(`/crm/rewards/${rewardId}/redeem`), {
    method: "POST",
    headers: requestHeaders(),
    body: JSON.stringify({ ticket: memberTicket }),
  });
  const body = (await response.json().catch(() => ({}))) as {
    ok?: boolean;
    voucher?: Voucher;
    pointsAfter?: number;
    error?: string;
  };
  if (!response.ok || !body.ok || !body.voucher || body.pointsAfter === undefined) {
    throw new Error(body.error ?? `sync-api /crm/rewards/${rewardId}/redeem responded ${response.status}`);
  }
  return { voucher: body.voucher, pointsAfter: body.pointsAfter };
}

export const promosCollection = psCollection("promos", promoSchema);
